import { Clock, ExternalLink, Tag, TrendingUp, Users } from "lucide-react";
import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";

import { FilterCategory } from "../filters/FilterCategory";
import { RecordTypeToggleList } from "../filters/RecordTypeToggleList";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { SettingsShortcut } from "../settings/SettingsShortcut";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActiveFilterChips } from "../misc/ActiveFilterBar";
import {
  LOOPS_OPTIONS,
  lastSeenOptions,
  useContactFilterBar,
} from "./contactFilterDescriptors";
import {
  readRecordTypeFilter,
  toggleRecordType,
  writeRecordTypeFilter,
} from "./recordTypeFilterValue";
import { TagFilterSelect } from "./TagFilterSelect";

export const ContactListFilter = () => {
  const { noteStatuses } = useConfigurationContext();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const { count, clearAll } = useActiveFilterChips(useContactFilterBar());
  const recordTypeSelection = readRecordTypeFilter(filterValues);

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.contacts.filters.search"),
      }}
      activeFilters={{ count, clearAll }}
    >
      {/* Panel order is deliberate, see LCATOMICZO-32. Eliot reported on 19 Aug
          that Status and Last activity pushed the two panels he uses, Record type
          and Tags, off the bottom of the sidebar. Record type leads because it is
          only a few rows, so it costs almost nothing above Tags. Do not re-sort
          this list alphabetically or back to its old shape.
          Record type takes several values at once (LCCRM-86). */}
      <FilterCategory
        icon={<Tag />}
        label="resources.contacts.filters.record_type"
        action={<SettingsShortcut section="record_types" />}
      >
        <RecordTypeToggleList
          className="w-full justify-between h-10 md:h-8"
          selected={recordTypeSelection}
          onToggle={(value) =>
            setFilters(
              writeRecordTypeFilter(
                filterValues,
                toggleRecordType(recordTypeSelection, value),
              ),
              [],
            )
          }
          size={isMobile ? "lg" : "sm"}
        />
      </FilterCategory>

      <FilterCategory
        label="resources.contacts.filters.tags"
        icon={<Tag />}
        action={<SettingsShortcut section="tags" />}
      >
        <TagFilterSelect />
      </FilterCategory>

      <FilterCategory
        label="resources.notes.fields.status"
        icon={<TrendingUp />}
        action={<SettingsShortcut section="note_statuses" />}
      >
        {noteStatuses.map((status) => (
          <ToggleFilterButton
            key={status.value}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={
              <span>
                {status.label} <Status status={status.value} />
              </span>
            }
            value={{ status: status.value }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        label="resources.contacts.fields.last_seen"
        icon={<Clock />}
      >
        {lastSeenOptions().map((option) => (
          <ToggleFilterButton
            key={option.label}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label={option.label}
            value={option.value}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<ExternalLink />}
        label="resources.contacts.filters.loops"
      >
        {LOOPS_OPTIONS.map((option) => (
          <ToggleFilterButton
            key={option.label}
            className="w-full justify-between h-10 md:h-8"
            label={option.label}
            value={option.value}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Users />}
        label="resources.contacts.fields.sales_id"
      >
        <ToggleFilterButton
          className="w-full justify-between h-10 md:h-8"
          label="crm.common.me"
          value={{ sales_id: identity?.id }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>
    </ResponsiveFilters>
  );
};
